// Runtime config for the embedded global-nav widget.
//
// The widget is mounted on the casino origin with two base URLs supplied as data attributes
// on its container (set by the host in casino/lib/casino_web/components/layouts.ex):
//   • appBase — core's Next.js frontend origin: the target of in-app navigation
//     (markets, profile, logout) and the host of the widget's own assets.
//   • apiBase — core's backend origin: data fetches and the Discord login redirect.
//
// nav-widget.tsx stashes these on window.__WEEABETS_NAV__ at boot; the Next-shim modules read
// them here so the shared Row 1 components behave correctly off-host.

export interface NavRuntime {
  appBase: string
  apiBase: string
}

declare global {
  interface Window {
    __WEEABETS_NAV__?: Partial<NavRuntime>
  }
}

export function navRuntime(): NavRuntime {
  const g = (typeof window !== 'undefined' && window.__WEEABETS_NAV__) || {}
  return { appBase: g.appBase ?? '', apiBase: g.apiBase ?? '' }
}

/**
 * Publish a widget mount's base URLs onto the shared `window.__WEEABETS_NAV__`. The nav and
 * sidebar widgets both mount independently, so this MERGES rather than overwrites: each base
 * prefers this mount's data attribute, then any value an earlier widget already set, then "".
 * That way a mount missing a data attribute can't clobber config the other widget supplied.
 */
export function applyRuntime(el: HTMLElement): void {
  if (typeof window === 'undefined') return
  const prev = window.__WEEABETS_NAV__ || {}
  window.__WEEABETS_NAV__ = {
    appBase: el.dataset.appBase || prev.appBase || '',
    apiBase: el.dataset.apiBase || el.dataset.appBase || prev.apiBase || '',
  }
}

function join(base: string, path: string): string {
  if (/^https?:\/\//.test(path)) return path
  return base.replace(/\/$/, '') + (path.startsWith('/') ? path : `/${path}`)
}

/** Resolve an in-app route (e.g. /markets/1) against core's frontend origin. */
export function appUrl(path: string): string {
  return join(navRuntime().appBase, path)
}

/** Resolve an API path (e.g. /api/auth/me) against core's backend origin. */
export function apiUrl(path: string): string {
  return join(navRuntime().apiBase, path)
}
